/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-return-assign */
import React, { useState, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Input from 'src/components/ui/Input/Input';
import debounce from 'lodash/debounce';

import classNames from 'classnames';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import styles from './FaqHelp.module.scss';
import { useFaqQuery } from '../../../services/faq';
import { components } from '../../../generated/v1';

type FaqItem = components['schemas']['Faq'];
interface AccordionSection {
  title: string;
  accordions: { title: string; content: string }[];
}

const SearchBox = ({ onSearch }: { onSearch: (query: string) => void }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const debouncedSearch = useCallback(
    debounce(query => {
      onSearch(query);
    }, 500),
    [onSearch],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValue(value);
    debouncedSearch(value);
  };

  return (
    <div className={styles.searchBox}>
      <div className={styles.searchBox_content}>
        <h2 className="typo-title-36 mb-16">{t('faq.title')}</h2>
        <span className="typo-title-16 mb-24">{t('faq.subTitle')}</span>
        <Input
          className="text-faded"
          placeholder={t('general.search_placeholder')}
          value={value}
          onChange={handleChange}
          clearable
          icon="search"
        />
      </div>
    </div>
  );
};

const FaqAccordion = ({ title, content }: { title: string; content: string }) => (
  <details className={styles.accordion}>
    <summary className={styles.accordionSummary}>
      <span className={styles.accordionTitle}>{title}</span>
      <Icon className={styles.accordionIcon} name="accordion-arrow-down" width={24} height={24} />
    </summary>
    <p className={styles.accordionContent}>{content}</p>
  </details>
);

const FaqAccordionSection = ({ title, accordions }: AccordionSection) => (
  <div className={styles.accordionSection}>
    <div className={styles.accordionSection_titleContainer}>
      <h3 className="typo-title-24 text-full pt-25 mb-4">{title}</h3>
    </div>
    <div className="flex-1">
      {accordions.map((accordion, index) => (
        <div key={accordion.title} className={`p-24 rounded-base ${index % 2 !== 0 && 'surface-elevated-more'}`}>
          <FaqAccordion title={accordion.title} content={accordion.content} />
        </div>
      ))}
    </div>
  </div>
);

const HelpBanner = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.banner}>
      <div className={styles.banner_logo}>
        <Icon name="logo-compact" width={32} height={32} />
      </div>
      <div className="flex flex-col flex-1">
        <span className="typo-text-16--bold mb-8">{t('faq.bannerTitle')}</span>
        <div className="flex">
          <span className="typo-text-12">
            <Trans
              i18nKey="faq.bannerText"
              components={{
                textLink: (
                  <a
                    className="typo-text-12--bold cursor-pointer underline"
                    href="/cases/create"
                    target="_blank"
                    rel="noreferrer noopener">
                    textLink
                  </a>
                ),
              }}
            />
          </span>
        </div>
      </div>
      <Button
        className="self-start lg:self-center"
        onClick={() => {
          window.location.href = 'mailto:alexander.schafschetzy@dynaflow.at';
        }}
        tone={ButtonToneProps.SECONDARY}
        title={t('general.get_in_touch')}
      />
    </div>
  );
};

const FaqHelp = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useFaqQuery();
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState<AccordionSection[]>([]);

  const mapFaqData = (faqItems: FaqItem[]): AccordionSection[] => {
    const grouped = faqItems.reduce<Record<string, AccordionSection>>((acc, item) => {
      const key = item.category_name ?? 'General';
      if (!acc[key]) {
        acc[key] = {
          title: key,
          accordions: [],
        };
      }
      acc[key].accordions.push({
        title: item.name ?? '',
        content: item.answer ?? '',
      });
      return acc;
    }, {});

    return Object.values(grouped);
  };

  useEffect(() => {
    if (data) {
      const allFaqs = mapFaqData(data.items ?? []);
      if (searchValue) {
        const filtered = allFaqs
          .map(section => ({
            ...section,
            accordions: section.accordions.filter(
              accordion =>
                accordion.title.toLowerCase().includes(searchValue.toLowerCase()) ||
                accordion.content.toLowerCase().includes(searchValue.toLowerCase()),
            ),
          }))
          .filter(section => section.accordions.length > 0);
        setFilteredData(filtered);
      } else {
        setFilteredData(allFaqs);
      }
    }
  }, [data, searchValue]);

  return (
    <div className={classNames(styles.root)}>
      <SearchBox onSearch={setSearchValue} />
      {isLoading && (
        <div className="flex justify-center py-64">
          <span className="typo-text-16--bold text-faded">{t('general.loading')}</span>
        </div>
      )}
      {isError && (
        <div className="flex justify-center py-64">
          <span className="typo-text-16--bold text-danger">{t('general.error_loading_data')}</span>
        </div>
      )}
      {!isLoading &&
        !isError &&
        filteredData.map((section, index) => (
          <div key={section.title}>
            <FaqAccordionSection {...section} />
            {index !== filteredData.length - 1 && <Divider />}
          </div>
        ))}
      {!isLoading && !isError && filteredData.length === 0 && (
        <div className="flex justify-center py-64">
          <span className="typo-text-16--bold text-faded">
            {searchValue ? `${t('faq.no_results_found')} "${searchValue}"` : t('faq.no_entries')}
          </span>
        </div>
      )}
      <HelpBanner />
    </div>
  );
};

export default FaqHelp;
